import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

// ════════════════════════════════════════════════════════════════════════
// Card — the base surface, editorial treatment (2026-06 refit).
//
// Flat by default: a hairline `border-rule` on `bg-surface`, a near-sharp
// `rounded-panel` (2px) edge, and NO shadow at rest (`shadow-rest`).
// Structure comes from the rule, not a drop shadow.
//   • `tone`        — paints a 3px left bar (board triage cue): good / flag / neutral.
//   • `interactive` — accent border on hover (no pillowy lift).
//   • `elevated`    — ONLY when a panel genuinely lifts off another panel.
//   • `padded`      — default p-4; pass false for flush content (tables, media).
// See docs/design/redesign-2026/USAGE-RULES.md §1–2, §5.
// ════════════════════════════════════════════════════════════════════════

export const cardVariants = cva(
  [
    "relative overflow-hidden bg-surface",
    "rounded-panel border border-rule shadow-rest",
    "transition-[border-color,box-shadow] duration ease-out",
  ].join(" "),
  {
    variants: {
      padded: {
        true: "p-4",
        false: "",
      },
      elevated: {
        true: "shadow-overlay",
        false: "",
      },
      interactive: {
        true: "cursor-pointer hover:border-accent",
        false: "",
      },
    },
    defaultVariants: {
      padded: true,
      elevated: false,
      interactive: false,
    },
  },
);

const toneBar: Record<NonNullable<CardProps["tone"]>, string> = {
  good: "bg-good",
  flag: "bg-flag",
  neutral: "bg-accent",
};

export interface CardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "color">,
    Omit<VariantProps<typeof cardVariants>, "padded" | "elevated" | "interactive"> {
  /** 3px left triage bar. */
  tone?: "good" | "flag" | "neutral";
  interactive?: boolean;
  elevated?: boolean;
  padded?: boolean;
  /** Semantic element to render. Defaults to a div. */
  as?: "div" | "article" | "section";
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      tone,
      interactive = false,
      elevated = false,
      padded = true,
      as: Tag = "div",
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <Tag
      ref={ref}
      className={cn(
        cardVariants({ padded, elevated, interactive }),
        // With default padding, inset the content past the tone bar so text
        // never overlaps it. When `padded={false}` the caller owns padding
        // and is responsible for clearing the bar (e.g. `pl-[calc(...)]`).
        tone && padded && "pl-[calc(theme(spacing.4)+var(--tone-bar))]",
        className,
      )}
      {...rest}
    >
      {tone && (
        <span
          aria-hidden="true"
          className={cn(
            "absolute inset-y-0 left-0 w-[var(--tone-bar)]",
            toneBar[tone],
          )}
        />
      )}
      {children}
    </Tag>
  ),
);
Card.displayName = "Card";
